import { getSessionMedia } from '../apis/demo-media/api';

function safeParseJson(value, fallback = null) {
  if (typeof value !== 'string' || value.trim() === '') {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
}

function storeSessionExitUrls(media) {
  if (!media) {
    return;
  }

  const { completeUrl, exitUrl } = media;
  if (completeUrl === undefined && exitUrl === undefined) {
    return;
  }

  const storedInitialConfig = safeParseJson(localStorage.getItem('initialConfig'), {});
  localStorage.setItem(
    'initialConfig',
    JSON.stringify({
      ...storedInitialConfig,
      completeUrl: completeUrl ?? storedInitialConfig?.completeUrl ?? null,
      exitUrl: exitUrl ?? storedInitialConfig?.exitUrl ?? null,
    })
  );
}

// completeUrl/exitUrl (where to send the patient when the session ends — see
// use-post-performance.jsx and exercise-setting-modal) come from the
// session-media endpoint rather than the socket, so /exercise can read them
// straight off initialConfig at mount with no live connection needed.
//
// One in-flight/settled fetch per session id, shared across every guest-flow
// page (camera-permission, try-demo) that calls this — same dedupe shape as
// useSessionMetadataStore's fetchMetadata, just a plain module cache since
// nothing renders off the result. Called as early as possible (camera-permission)
// for the most dwell time to finish in, and again from try-demo (covers a
// deep link straight onto /try-demo) — the second call reuses this same
// promise instead of firing a second request.
const fetchesBySessionId = new Map();

export function ensureSessionExitUrlsFetched(sessionId) {
  if (!sessionId) {
    return Promise.resolve();
  }

  if (!fetchesBySessionId.has(sessionId)) {
    const promise = getSessionMedia(sessionId)
      .then(storeSessionExitUrls)
      .catch(() => {
        // Best-effort — a failed lookup just leaves completeUrl/exitUrl unset,
        // same as if the backend never returned them. Drop the cached promise
        // so a later call (e.g. from /try-demo) can retry instead of being
        // stuck reusing this failure forever.
        fetchesBySessionId.delete(sessionId);
      });
    fetchesBySessionId.set(sessionId, promise);
  }

  return fetchesBySessionId.get(sessionId);
}
